/**
 * Relatório consolidado: várias auditorias salvas de um estoque, somadas.
 *
 * ## O que "somar ciclos" significa aqui
 *
 * Cada ciclo é uma **medição independente**. Um produto contado no ciclo 1 e de novo no
 * ciclo 2 conta **duas vezes** — são duas medições, não um produto duplicado.
 *
 * É de propósito, e é o que mede acurácia de inventário ao longo do tempo: de todas as
 * contagens feitas no período, quantas bateram com o sistema. Deduplicar por produto
 * responderia outra pergunta ("quantos produtos distintos foram tocados"), que não é a que
 * um relatório de auditoria faz.
 *
 * ⚠️ Por isso a tela e o PDF precisam dizer **"medições"**, e não "produtos": ler
 * "3.200 itens contados" como "o estoque tem 3.200 produtos" é o mal-entendido natural, e
 * caro num relatório que circula.
 *
 * ## A armadilha dos percentuais
 *
 * O percentual do total é recalculado a partir das **somas**, nunca pela média dos
 * percentuais de cada ciclo. Média de percentual dá peso igual a um ciclo de 10 itens e a
 * um de 2.000 — e produz um número que parece razoável e está errado. Há teste para isso.
 */
import { rotuloDaAuditoria } from './lote.js';
/** Uma casa decimal, como o resto do projeto. `0` quando não há base. */
export function percentual(parte, total) {
    if (total <= 0)
        return 0;
    return parseFloat(((parte / total) * 100).toFixed(1));
}
function doCiclo(a) {
    const e = a.estatisticas;
    const corrigidos = e.corrigidos ?? { total: 0, corretos: 0, incorretos: 0 };
    return {
        // `Auditoria.id` é opcional no tipo — `montarAuditoria` devolve `Omit<Auditoria, 'id'>`,
        // porque o id só existe depois de gravar. Consolidar só recebe auditoria já salva, mas
        // o padrão evita que a tela use `undefined` como chave de lista.
        auditoriaId: a.id ?? '',
        lote: (a.lote ?? '').trim(),
        nome: a.nome,
        ciclo: a.contagemCycle,
        data: a.data,
        total: e.total ?? 0,
        contados: e.contados ?? 0,
        naoContados: e.naoContados ?? 0,
        corretos: e.corretos ?? 0,
        incorretos: e.incorretos ?? 0,
        corrigidos: corrigidos.total ?? 0,
        corrigidosCorretos: corrigidos.corretos ?? 0,
        corrigidosIncorretos: corrigidos.incorretos ?? 0,
        foraDoErp: e.foraDoErp ?? 0,
        percentualCorretos: percentual(e.corretos ?? 0, e.contados ?? 0),
        percentualIncorretos: percentual(e.incorretos ?? 0, e.contados ?? 0),
        percentualCobertura: percentual((e.contados ?? 0) + (corrigidos.total ?? 0), e.total ?? 0),
    };
}
/**
 * Consolida as auditorias escolhidas.
 *
 * Aceita a lista em qualquer ordem e devolve por ciclo crescente: um relatório de evolução
 * lido de trás para frente não é relatório de evolução.
 *
 * As estatísticas vêm do que foi **gravado** em cada auditoria, não recalculadas do
 * snapshot de produtos. É deliberado: a auditoria é um retrato do que valia no fechamento,
 * e recalcular hoje aplicaria as regras de hoje a um dado de meses atrás — o número mudaria
 * sozinho a cada alteração no cálculo.
 */
export function consolidarCiclos(auditorias) {
    const ciclos = auditorias
        .map(doCiclo)
        .sort((a, b) => a.ciclo - b.ciclo || a.data.getTime() - b.data.getTime());
    const soma = (campo) => ciclos.reduce((acc, c) => acc + (typeof c[campo] === 'number' ? c[campo] : 0), 0);
    const contados = soma('contados');
    const total = soma('total');
    const corretos = soma('corretos');
    const incorretos = soma('incorretos');
    return {
        ciclos,
        totais: {
            ciclos: ciclos.length,
            total,
            contados,
            naoContados: soma('naoContados'),
            corretos,
            incorretos,
            corrigidos: soma('corrigidos'),
            corrigidosCorretos: soma('corrigidosCorretos'),
            corrigidosIncorretos: soma('corrigidosIncorretos'),
            foraDoErp: soma('foraDoErp'),
            // Das SOMAS, nunca da média dos percentuais de cada ciclo — ver o topo do arquivo.
            percentualCorretos: percentual(corretos, contados),
            percentualIncorretos: percentual(incorretos, contados),
            percentualCobertura: percentual(contados + soma('corrigidos'), total),
        },
    };
}
/**
 * O período do relatório, das pontas que chegam na ordem do `consolidarCiclos`:
 * `Lote A a Lote B · d1 a d2`. Sem lote em alguma ponta, só `d1 a d2`: o contador interno
 * não vai à tela.
 */
export function periodoDoConsolidado(ciclos) {
    const primeiro = ciclos[0];
    const ultimo = ciclos[ciclos.length - 1];
    if (!primeiro || !ultimo)
        return 'Nenhuma rodada selecionada';
    const datas = `${primeiro.data.toLocaleDateString('pt-BR')} a ${ultimo.data.toLocaleDateString('pt-BR')}`;
    return primeiro.lote.trim() && ultimo.lote.trim()
        ? `${rotuloDaAuditoria(primeiro)} a ${rotuloDaAuditoria(ultimo)} · ${datas}`
        : datas;
}
/**
 * Ids das auditorias que repetem `inventoryId` + `contagemCycle` de outra da lista e não são a que fica.
 * Fica a de id `{inventoryId}_{contagemCycle}` (o id fixo do Finalizar desde a 2.20); sem ela, a de `data`
 * mais recente; na mesma data, a primeira da lista. Grupo de uma só nunca entra.
 *
 * O formato do id repete de propósito o `idAuditoria` de `apps/web/src/lib/auditorias-repo.ts`
 * (o shared não importa do web): se um mudar, o outro muda junto. Deduplicar às cegas poderia
 * tirar uma rodada legítima, por isso a tela só desmarca e nada é apagado.
 */
export function duplicadasDoConsolidado(auditorias) {
    const grupos = new Map();
    for (const a of auditorias) {
        const chave = `${a.inventoryId}|${a.contagemCycle}`;
        grupos.set(chave, [...(grupos.get(chave) ?? []), a]);
    }
    const duplicadas = new Set();
    for (const grupo of grupos.values()) {
        if (grupo.length < 2)
            continue;
        const fixa = grupo.find((a) => a.id === `${a.inventoryId}_${a.contagemCycle}`);
        // reduce com `>` estrito: no empate de data fica a primeira da lista.
        const fica = fixa ?? grupo.reduce((m, a) => (a.data.getTime() > m.data.getTime() ? a : m));
        for (const a of grupo)
            if (a !== fica)
                duplicadas.add(a.id ?? '');
    }
    return duplicadas;
}
//# sourceMappingURL=consolidado.js.map